import { createContext, useContext, useCallback, useEffect, useReducer, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { modalManager } from 'utils';
import { networkResponseLisenner } from 'utils/api';
import run from 'services/run';
import { Loader } from 'components';
import { currentGameConfig } from 'utils/handleCurrentContentConfig';

const appState = {
  isError: false,
  isInited: false,
  isLoading: false,
  isMenuOpen: false,
  modal: {
    type: 'info',
    header: '',
    component: null,
    data: {},
    isOpen: false,
    allowClose: false,
  },
  user: null,
  store: null,
  storeEvent: 1,
  storeType: 'E',
  selectedStorePackage: null,
  status: '',
  dailyBonusTime: null,
  vipBonusTime: null,
  vipBonusClaimed: false,
};

export const appActions = {
  setError: 'setError',
  setStoreType: 'setStoreType',
  setStatus: 'setStatus',
  multiUpdate: 'multiUpdate',
  selectStorePackage: 'selectStorePackage',
  setUserData: 'setUserData',
  setModal: 'setModal',
  setDailyBonusTime: 'setDailyBonusTime',
  setVipBonusTime: 'setVipBonusTime',
  setVipBonusClaimed: 'setVipBonusClaimed',
};

const reducer = (state, action) => {
  switch (action.type) {
    case appActions.setError:
      return { ...state, isError: action.value };
    case appActions.setStoreType:
      return { ...state, storeType: action.value, isError: false };
    case appActions.setStatus:
      return { ...state, setStatus: action.value };
    case appActions.multiUpdate:
      return { ...state, ...action.value };
    case appActions.selectStorePackage:
      return { ...state, selectedStorePackage: action.value, store: null, storeType: '-1' };
    case appActions.setUserData:
      return { ...state, user: action.value };
    case appActions.setModal:
      return { ...state, modal: { ...state.modal, ...action.value } };
    case appActions.setDailyBonusTime:
      return { ...state, dailyBonusTime: action.value };
    case appActions.setVipBonusTime:
      return { ...state, vipBonusTime: action.value };
    case appActions.setVipBonusClaimed:
      return { ...state, vipBonusClaimed: action.value };
    default:
      throw new Error();
  }
};

const AppStateContext = createContext();
AppStateContext.displayName = 'AppStateContext';

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, appState);
  if (process.env.NODE_ENV === 'development') {
    window.state = state;
  }
  const navigate = useNavigate();

  const networkResponseList = useSyncExternalStore(
    networkResponseLisenner.addListener,
    networkResponseLisenner.get,
  );

  const openModal = useCallback(({ modal, options, data }) => {
    const modalProps = modalManager[modal];
    update(appActions.setModal, { ...modalProps, ...options, data, isOpen: true });
  }, []);

  const handleNetworkStatus = useCallback(async response => {
    const { status, url } = response;
    if (url && url?.includes(currentGameConfig.api.internalAuth) && status === 401) {
      navigate('/', { replace: true });
      return;
    }
    switch (status) {
      case 400:
        navigate('/', { replace: true });
        break;
      case 401: {
        try {
          const data = await response.json();
          const key = data ? Object.keys(data)[0] : null;
          const eNr = key && data[key] ? data[key].eNr : null;
          if (eNr === 801 || eNr === 800) openModal({ modal: 'loginSessionExpired' });
          if (eNr === 803) openModal({ modal: 'banned' });
        } catch (error) {
          openModal({ modal: 'loginSessionExpired' });
        }
        break;
      }
      case 404:
        openModal({ modal: 'userNotFound' });
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    const numItems = networkResponseList.length;
    if (numItems > 0) {
      handleNetworkStatus(networkResponseList[numItems - 1]);
    }
  }, [networkResponseList, handleNetworkStatus]);

  useEffect(() => {
    run({ eId: state.storeEvent, hashParams: window.location.hash }).then(res =>
      dispatch({ type: appActions.multiUpdate, value: { ...res, isInited: true } }),
    );
  }, []);

  const update = (type = '', value = null) => dispatch({ type, value });

  const setGlobalError = useCallback(sts => update(appActions.setError, sts), []);
  const setStoreType = useCallback(storeType => update(appActions.setStoreType, storeType), []);
  const setStoreData = useCallback(data => update(appActions.multiUpdate, data), []);
  const selectStorePackage = useCallback(data => update(appActions.selectStorePackage, data), []);
  const setUserData = useCallback(data => update(appActions.setUserData, data), []);
  const resetAppState = useCallback(
    () => update(appActions.multiUpdate, { ...appState, isInited: true }),
    [],
  );
  const closeModal = useCallback(() => update(appActions.setModal, appState.modal), []);
  const setDailyBonusTime = useCallback(timestamp => update(appActions.setDailyBonusTime, timestamp), []);
  const setVipBonusTime = useCallback(timestamp => update(appActions.setVipBonusTime, timestamp), []);
  const setVipBonusClaimed = useCallback(claimed => update(appActions.setVipBonusClaimed, claimed), []);

  const value = {
    state,
    setStoreType,
    setStoreData,
    selectStorePackage,
    setUserData,
    resetAppState,
    setGlobalError,
    openModal,
    closeModal,
    setDailyBonusTime,
    setVipBonusTime,
    setVipBonusClaimed,
  };

  return (
    <AppStateContext.Provider value={value}>
      {state.isInited ? children : <Loader fullHeight />}
    </AppStateContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    console.error('useApp must be used within an AppProvider');
    return;
  }
  return context;
};
